import type { EventFeatureFilter, FeatureType } from '@/models';
import { ALL_FEATURE_TYPES, FEATURE_TYPE_LABELS } from '@/models';

interface EventFeatureFilterEditorProps {
  isPublic: boolean;
  onIsPublicChange: (isPublic: boolean) => void;
  features: FeatureType[];
  onFeaturesChange: (features: FeatureType[]) => void;
  excludeCommunityMembers: boolean;
  onExcludeCommunityMembersChange: (value: boolean) => void;
  onlyCommunityMembers: boolean;
  onOnlyCommunityMembersChange: (value: boolean) => void;
  disabled: boolean;
  entityLabel: string; // "Event" or "Serie" - used in the visible labels
}

// Builds the payload value for the featureFilter field: null means public
export function buildEventFeatureFilter(
  isPublic: boolean,
  features: FeatureType[],
  excludeCommunityMembers: boolean,
  onlyCommunityMembers: boolean
): EventFeatureFilter | null {
  if (isPublic) return null;

  return { features, excludeCommunityMembers, onlyCommunityMembers };
}

// A single pill-style toggle switch, matching the look used across the event modals
export function ToggleSwitch({ checked, onChange, disabled, className = '' }: { checked: boolean; onChange: () => void; disabled: boolean; className?: string }) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
        checked ? 'bg-green-600 dark:bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
      } ${className}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

export default function EventFeatureFilterEditor({
  isPublic,
  onIsPublicChange,
  features,
  onFeaturesChange,
  excludeCommunityMembers,
  onExcludeCommunityMembersChange,
  onlyCommunityMembers,
  onOnlyCommunityMembersChange,
  disabled,
  entityLabel
}: EventFeatureFilterEditorProps) {
  const toggleFeature = (featureType: FeatureType) => {
    onFeaturesChange(
      features.includes(featureType)
        ? features.filter(f => f !== featureType)
        : [...features, featureType]
    );
  };

  // The two community switches are mutually exclusive - the backend would otherwise
  // build a filter that can never match anybody
  const toggleExclude = () => {
    const next = !excludeCommunityMembers;
    onExcludeCommunityMembersChange(next);
    if (next) onOnlyCommunityMembersChange(false);
  };

  const toggleOnly = () => {
    const next = !onlyCommunityMembers;
    onOnlyCommunityMembersChange(next);
    if (next) onExcludeCommunityMembersChange(false);
  };

  return (
    <>
      {/* Public Toggle */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Öffentliche {entityLabel}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Für alle Nutzer sichtbar (keine Einschränkungen)
            </p>
          </div>
          <ToggleSwitch checked={isPublic} onChange={() => onIsPublicChange(!isPublic)} disabled={disabled} />
        </div>
      </div>

      {/* Features & audience - only relevant when not public */}
      {!isPublic && (
        <>
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Features *
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Nur Nutzer mit mindestens einem dieser Features sehen {entityLabel === 'Serie' ? 'die Serie' : 'das Event'}
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto pl-1 -ml-1 pb-2">
              {ALL_FEATURE_TYPES.map((type) => (
                <div key={type} className="flex items-center">
                  <ToggleSwitch
                    checked={features.includes(type)}
                    onChange={() => toggleFeature(type)}
                    disabled={disabled}
                    className="mr-3"
                  />
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {FEATURE_TYPE_LABELS[type]}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Community-Mitglieder
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Zusätzliche Einschränkung auf bzw. gegen Community-Mitglieder
            </p>
            <div className="space-y-2">
              <div className="flex items-center">
                <ToggleSwitch checked={excludeCommunityMembers} onChange={toggleExclude} disabled={disabled} className="mr-3" />
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Community-Mitglieder ausschließen
                </label>
              </div>
              <div className="flex items-center">
                <ToggleSwitch checked={onlyCommunityMembers} onChange={toggleOnly} disabled={disabled} className="mr-3" />
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Nur Community-Mitglieder
                </label>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
